import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Truck, HardHat, LayoutGrid, Hammer, ShieldCheck, Zap, BadgeCheck, Phone, Mail, Users } from "lucide-react";
import Navbar from "@/components/karmivo/Navbar";
import HindiWarning from "@/components/karmivo/HindiWarning";
import { SERVICES, api, BACKEND_URL } from "@/lib/api";

const ICONS = { truck: Truck, "hard-hat": HardHat, "layout-grid": LayoutGrid, hammer: Hammer };
const DEFAULT_HERO = "https://images.unsplash.com/photo-1772621676358-0191ab4df9ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const abs = (u) => (u ? `${BACKEND_URL}${u}` : null);

export default function Landing() {
  const [company, setCompany] = useState(null);
  const [images, setImages] = useState(null);
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    api.get(`/settings/public/company`).then(({ data }) => setCompany(data)).catch(() => setCompany(null));
    api.get(`/settings/public/images`).then(({ data }) => setImages(data)).catch(() => setImages(null));
    api.get(`/leadership/public`).then(({ data }) => setLeaders(Array.isArray(data) ? data : [])).catch(() => setLeaders([]));
  }, []);

  const heroImg = abs(images?.hero?.url) || DEFAULT_HERO;
  const heroAlt = images?.hero?.alt || "Workers in Jaipur";
  const brand = (company?.name || "Karmivo").toUpperCase();
  const tagline = company?.tagline;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 karmivo-grid-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wider mb-4">
              <span className="w-2 h-2 rounded-full bg-orange-500 pulse-dot" /> Live in Jaipur, Rajasthan
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05] tracking-tight">
              {tagline ? (
                <>
                  {tagline.split(".")[0]}.<br/>
                  {tagline.split(".").slice(1).join(".").trim() && <span className="text-orange-600">{tagline.split(".").slice(1).join(".").trim()}</span>}
                </>
              ) : (
                <>Reliable Labour.<br/><span className="text-orange-600">Smartly Dispatched.</span></>
              )}
            </h1>
            <p className="mt-5 text-slate-600 text-lg max-w-xl">
              {brand.charAt(0) + brand.slice(1).toLowerCase()} is a managed workforce platform. Book verified workers or an entire team for loading, daily labour, tiles work and renovation — assigned by our AI dispatch engine.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book"><Button size="lg" className="bg-orange-500 hover:bg-orange-600 h-12 px-6 text-base shadow-lg shadow-orange-200" data-testid="hero-book-labour-btn">Book Labour</Button></Link>
              <Link to="/track"><Button size="lg" variant="outline" className="h-12 px-6 text-base" data-testid="hero-track-btn">Track Booking</Button></Link>
              <Link to="/worker/register"><Button size="lg" variant="ghost" className="h-12 px-6 text-base" data-testid="hero-join-worker-btn">Join as Worker</Button></Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><BadgeCheck size={16} className="text-emerald-500"/> KYC Verified Workers</span>
              <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-emerald-500"/> Secure Online Payments</span>
              <span className="flex items-center gap-1.5"><Zap size={16} className="text-emerald-500"/> AI Dispatch</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-orange-200 via-amber-100 to-transparent rounded-3xl blur-2xl opacity-70" />
            <img
              src={heroImg}
              alt={heroAlt}
              data-testid="hero-image"
              className="relative rounded-3xl shadow-2xl border-4 border-white w-full object-cover aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-16">
        <HindiWarning />
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Services</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">Four services. One trusted team.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon];
            const imgUrl = abs(images?.[`service_${s.key}`]?.url);
            const imgAlt = images?.[`service_${s.key}`]?.alt || s.label;
            return (
              <Link to={`/book?service=${s.key}`} key={s.key} data-testid={`service-card-${s.key}`}>
                <Card className="h-full hover:shadow-xl hover:-translate-y-1 transition-all border-slate-200 group cursor-pointer overflow-hidden">
                  {imgUrl ? (
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                      <img src={imgUrl} alt={imgAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
                    </div>
                  ) : (
                    <div className="p-6 pb-0">
                      <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition">
                        <Icon size={24} />
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    {imgUrl && (
                      <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
                        <Icon size={18} />
                      </div>
                    )}
                    <h3 className="font-display font-bold text-lg text-slate-900">{s.label}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                    <div className="mt-4 text-xs font-semibold text-orange-600 uppercase tracking-wider">Book Now →</div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-900 text-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          {[
            { n: "01", t: "Book & Pay", d: "Choose service, workers, and schedule. Pay online — never in cash." },
            { n: "02", t: "AI Dispatch", d: "Our engine scores and assigns the best verified workers to your job." },
            { n: "03", t: "Closure Code", d: "Share your 6-digit code after work is completed. That's it." },
          ].map(s => (
            <div key={s.n}>
              <div className="font-mono text-orange-500 text-sm">{s.n}</div>
              <div className="font-display text-2xl font-bold mt-2">{s.t}</div>
              <p className="text-slate-400 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {leaders.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-20" data-testid="leadership-section">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-widest text-orange-600 font-bold flex items-center gap-2"><Users size={14}/> Leadership</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">Leadership & Board of Directors</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {leaders.map((p) => (
              <Card key={p.id} className="overflow-hidden border-slate-200 hover:shadow-xl transition-shadow" data-testid={`leader-card-${p.id}`}>
                {p.photo_url ? (
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img src={abs(p.photo_url)} alt={p.name} className="w-full h-full object-cover"/>
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-display text-2xl font-bold">
                      {p.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-display font-bold text-lg text-slate-900">{p.name}</h3>
                  <div className="text-xs uppercase tracking-wider text-orange-600 font-semibold mt-0.5">{p.designation}</div>
                  {p.short_bio && <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.short_bio}</p>}
                  {p.experience && <p className="mt-2 text-xs text-slate-500 leading-relaxed">{p.experience}</p>}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      <footer className="bg-slate-950 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {company?.logo_url
              ? <img src={abs(company.logo_url)} alt={brand} className="h-7 object-contain" data-testid="footer-logo"/>
              : <div className="w-7 h-7 rounded-md bg-orange-500 text-white flex items-center justify-center"><Truck size={16}/></div>}
            <span className="font-display font-bold text-white">{brand}</span>
            <span className="text-xs">© 2026</span>
          </div>
          <div className="text-xs flex flex-wrap items-center gap-4">
            {company?.phone && <span className="flex items-center gap-1" data-testid="footer-phone"><Phone size={12}/> Support: {company.phone}</span>}
            {company?.email && <a href={`mailto:${company.email}`} className="flex items-center gap-1 hover:text-white" data-testid="footer-email"><Mail size={12}/> {company.email}</a>}
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
